import { useState } from "react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";

// Extra controls on two checklist lights: remove the test ZIP region, and check the X-Forwarded-For chain.
export function RemoveTestRegion({ onDone }) {
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => { const r = await api("/admin/checklist/remove-test-region", { body: {} }); setRes(r); onDone(); };
  return (
    <div className="mt-2 space-y-2" data-testid="checklist-remove-test-region">
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="outline" className="cw-error" onClick={() => setOpen(true)} data-testid="checklist-remove-test-region-button">Remove test region</Button>
        <span className="cw-muted text-xs" data-testid="checklist-dev-on-zips-reminder">Also clear DEV_ON_ZIPS in deployment secrets.</span>
      </div>
      {res && (
        <div className="cw-tone-success rounded-xl px-3 py-2 text-xs" role="status" data-testid="checklist-remove-test-region-result">
          <p>Removed: {res.removed_regions?.length ? res.removed_regions.map((r) => `${r.label || r.id}${r.deleted ? "" : " (99999 taken out)"}`).join(", ") : "no regions had 99999"}. DEV_ON_ZIPS is now ignored.</p>
          {res.dev_on_zips_env?.length > 0 && <p className="mt-1">DEV_ON_ZIPS still set to {res.dev_on_zips_env.join(", ")} in the environment.</p>}
          {res.note && <p className="mt-1">{res.note}</p>}
        </div>
      )}
      <ConfirmDialog open={open} onOpenChange={setOpen} onConfirm={run} testId="checklist-remove-test-region-dialog" confirmLabel="Remove test region"
        title="Remove the test ZIP 99999?" description="Regions containing 99999 are removed (or have 99999 taken out), and DEV_ON_ZIPS is ignored from now on. Test accounts in 99999 will no longer be in an On area." />
    </div>
  );
}

const ROWS = [["resolved_client_ip", "Client IP the server sees"], ["trusted_proxy_hops", "TRUSTED_PROXY_HOPS now"], ["hop_count", "Hops in X-Forwarded-For"],
  ["x_forwarded_for", "X-Forwarded-For (left → right)"], ["x_real_ip", "X-Real-IP"], ["socket_peer", "Direct connection from"]];

export function IpCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => { setBusy(true); setError(null); try { setRes(await api("/admin/diagnostics/ip")); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const show = (v) => (Array.isArray(v) ? (v.length ? v.join("  →  ") : "empty") : v == null || v === "" ? "—" : String(v));
  return (
    <div className="mt-2" data-testid="checklist-ip-check">
      <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="checklist-ip-check-button">{busy ? "Checking…" : "Check IP chain"}</Button>
      {error && <p className="cw-error mt-2 text-xs" role="alert">{error}</p>}
      {res && (
        <div className="mt-3 rounded-xl bg-[var(--cw-surface-tertiary)] p-4 text-xs" data-testid="checklist-ip-result">
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-[200px_1fr]">
            {ROWS.map(([k, l]) => <div key={k} className="contents"><dt className="cw-muted">{l}</dt><dd className="break-all font-mono" data-testid={`ip-${k}`}>{show(res[k])}</dd></div>)}
          </dl>
          {res.how_to && <p className="cw-soft mt-3 leading-5">{res.how_to}</p>}
        </div>
      )}
    </div>
  );
}

const KEY_LABEL = { account_sid: "Account SID", auth_token: "Auth Token", verify_service_sid: "Verify Service SID" };
const PROVIDER = { test: "Test mode (fixed code, no texts sent)", twilio: "Twilio Verify (real texts)", unavailable: "Unavailable (test mode off, Twilio keys missing)" };
const Dot = ({ ok, id }) => <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${ok ? "bg-[var(--cw-success)]" : "bg-[var(--cw-error)]"}`} role="img" aria-label={ok ? "Present" : "Missing"} data-testid={id} />;

function twilioLine(c) {
  if (c.skipped) return { ok: null, text: `Not checked: ${c.skipped}.` };
  if (c.ok) return { ok: true, text: `OK${c.service_name ? ` · service “${c.service_name}”` : ""}${c.status_code ? ` (HTTP ${c.status_code})` : ""}` };
  const hint = c.status_code === 401 ? "Account SID or Auth Token is wrong." : c.status_code === 404 ? "Verify Service SID is wrong." : c.error ? `Could not reach Twilio (${c.error}).` : "Check the three Twilio values.";
  return { ok: false, text: `Failed${c.status_code ? ` (HTTP ${c.status_code})` : ""}. ${hint}` };
}

// Owner-only: GET /admin/diagnostics/sms. Shows key presence + env names only, never values.
export function SmsCheck() {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => { setBusy(true); setError(null); try { setRes(await api("/admin/diagnostics/sms")); } catch (e) { setError(e.message); } finally { setBusy(false); } };
  const tw = res && twilioLine(res.twilio_check || {});
  return (
    <div className="mt-3 rounded-2xl border border-border bg-white p-4" data-testid="checklist-sms-check">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold">Text-message (SMS) check</p>
        <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="checklist-sms-check-button">{busy ? "Checking…" : "Run SMS check"}</Button>
      </div>
      {error && <p className="cw-error mt-2 text-xs" role="alert" data-testid="checklist-sms-check-error">{error}</p>}
      {res && (
        <div className="mt-3 space-y-3 text-xs" data-testid="checklist-sms-result">
          <p>Provider in use: <span className="font-semibold" data-testid="sms-provider">{PROVIDER[res.provider] || res.provider}</span>
            <span className="cw-muted"> · SMS_TEST_MODE {res.sms_test_mode ? "on" : "off"} · setting sms_provider = {res.setting_sms_provider}</span></p>
          <ul className="space-y-1.5" data-testid="sms-keys">
            {Object.entries(res.keys || {}).map(([k, v]) => (
              <li key={k} className="flex items-center gap-2" data-testid={`sms-key-${k}`}>
                <Dot ok={v.present} id={`sms-key-dot-${k}`} />
                <span className="w-36 font-medium">{KEY_LABEL[k] || k}</span>
                <span className={v.present ? "cw-success" : "cw-error"}>{v.present ? "Present" : "Missing"}</span>
                <span className="cw-muted font-mono">{v.present ? `(${v.env_name})` : `(set ${v.accepted_names?.[0]})`}</span>
              </li>
            ))}
          </ul>
          <p className="flex items-center gap-2" data-testid="sms-twilio-check">
            {tw.ok !== null && <Dot ok={tw.ok} id="sms-twilio-dot" />}<span className="font-medium">Twilio connection:</span><span className={tw.ok === false ? "cw-error" : tw.ok ? "cw-success" : "cw-soft"}>{tw.text}</span>
          </p>
          {res.how_to && <p className="cw-soft leading-5">{res.how_to}</p>}
        </div>
      )}
    </div>
  );
}
